import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { css, keyframes } from '@emotion/react';
import { useMemo } from 'react';
import { isConfigFlagEnabled } from '../config';
import { useIsRowLayoutMode } from '../hooks/useIsRowLayoutMode';
import { useAppSelector } from '../redux/store';
import { LobbyChatPanel } from '../scenes/lobby/panels/lobbyChatPanel';
import { MttStagingChatPanel } from '../scenes/mttStaging/panels/mttStagingChatPanel';
import { BombsPanel } from '../scenes/poker/panels/bombsPanel/bombsPanel';
import { DebugPanel } from '../scenes/poker/panels/debugPanel/debugPanel';
import { HistoryPanel } from '../scenes/poker/panels/historyPanel/historyPanel';
import { PokerChatPanel } from '../scenes/poker/panels/pokerChatPanel';
import { PokerRoomInfoPanel } from '../scenes/poker/panels/pokerRoomInfoPanel';
import { SideBetsPanel } from '../scenes/poker/panels/sideBetsPanel/sideBetsPanel';
import { TourneyStandingsPanel } from '../scenes/poker/panels/tourneyStandingsPanel/tourneyStandingsPanel';
import { ALL_PANEL_PROPERTIES } from '../types/panelTypes';
import { assertUnreachable } from '../utils/tsUtils';
import { isTestEnvironment } from '../utils/utils';
import { AdminPanel } from './adminPanel/adminPanel';
import { DirectMessagesPanel } from './directMessagesPanel/directMessagesPanel';
import { FriendsPanel } from './friendsPanel/friendsPanel';
import { PlayerInfoPanel } from './playerInfoPanel/playerInfoPanel';
import { PlayersPanel } from './playersPanel/playersPanel';
import { SelfPanel } from './selfPanel/selfPanel';
const ANIMATION_DELTA_PX = 20;
const ANIMATION_DURATION_MS = 200;
const IS_ANIMATION_ENABLED = isConfigFlagEnabled('panelAnimations') && !isTestEnvironment();
function renderPanel(activePanelType, roomSceneType) {
    switch (activePanelType) {
        case 'chat': {
            switch (roomSceneType) {
                case 'lobby': {
                    return _jsx(LobbyChatPanel, {});
                }
                case 'poker': {
                    return _jsx(PokerChatPanel, {});
                }
                case 'mttStaging': {
                    return _jsx(MttStagingChatPanel, {});
                }
                default: {
                    assertUnreachable(roomSceneType);
                }
            }
            break;
        }
        case 'pokerRoomInfo': {
            return _jsx(PokerRoomInfoPanel, {});
        }
        case 'players': {
            return _jsx(PlayersPanel, {});
        }
        case 'friends': {
            return _jsx(FriendsPanel, {});
        }
        case 'directMessages': {
            return _jsx(DirectMessagesPanel, {});
        }
        case 'playerInfo': {
            return _jsx(PlayerInfoPanel, {});
        }
        case 'self': {
            return _jsx(SelfPanel, {});
        }
        case 'tourneyStandings': {
            return _jsx(TourneyStandingsPanel, {});
        }
        case 'bombs': {
            return _jsx(BombsPanel, {});
        }
        case 'sideBets': {
            return _jsx(SideBetsPanel, {});
        }
        case 'history': {
            return _jsx(HistoryPanel, {});
        }
        case 'debug': {
            return _jsx(DebugPanel, {});
        }
        case 'admin': {
            return _jsx(AdminPanel, {});
        }
        default: {
            assertUnreachable(activePanelType);
        }
    }
}
const ROW_MODE_KEYFRAMES = keyframes `
  0% {
    transform: translateX(${ANIMATION_DELTA_PX}px);
  }
  100% {
    transform: none;
  }
  `;
const COLUMN_MODE_KEYFRAMES = keyframes `
  0% {
    transform: translateY(-${ANIMATION_DELTA_PX}px);
  }
  100% {
    transform: none;
  }
`;
export const PanelRouter = () => {
    const activePanelType = useAppSelector((state) => state.panels.activePanelType);
    const roomSceneType = useAppSelector((state) => state.scene.roomSceneType);
    const isRowLayoutMode = useIsRowLayoutMode();
    const applyColorsToContainer = roomSceneType === 'poker';
    const backgroundColor = activePanelType && ALL_PANEL_PROPERTIES[activePanelType].backgroundColor;
    const borderColor = activePanelType && ALL_PANEL_PROPERTIES[activePanelType].borderColor;
    const colorStyles = useMemo(() => {
        return css(backgroundColor &&
            css `
          background-color: ${backgroundColor};
        `, borderColor &&
            css(css `
            border-color: ${borderColor};
            border-style: solid;
          `, isRowLayoutMode
                ? css `
                border-width: 0 0 0 2px;
              `
                : css `
                border-width: 0 0 2px 0;
              `));
    }, [backgroundColor, borderColor, isRowLayoutMode]);
    const containerCss = useMemo(() => {
        return css(css `
        width: 100%;
        height: 100%;
      `, applyColorsToContainer && colorStyles);
    }, [applyColorsToContainer, colorStyles]);
    const contentsCss = useMemo(() => {
        return css(css `
        width: 100%;
        height: 100%;
      `, !applyColorsToContainer && colorStyles, IS_ANIMATION_ENABLED &&
            css `
          animation: ${isRowLayoutMode ? ROW_MODE_KEYFRAMES : COLUMN_MODE_KEYFRAMES} ${ANIMATION_DURATION_MS}ms forwards;
        `);
    }, [applyColorsToContainer, colorStyles, isRowLayoutMode]);
    if (!activePanelType) {
        return null;
    }
    if (!roomSceneType) {
        return null;
    }
    return (_jsx("div", { "data-testid": 'panel-container', css: containerCss, children: _jsx("div", { css: contentsCss, children: renderPanel(activePanelType, roomSceneType) }) }, activePanelType));
};
